import type { ComponentType } from 'react';
import { Skeleton } from '@/components/Skeleton';
import { lazyComponent } from './lazyWithPreload';

/**
 * Every routed view that is not mail, as a lazy chunk (issue #208).
 *
 * Element-level (`React.lazy` + `Suspense`) rather than the data router's
 * route `lazy`, deliberately: route `lazy` holds the navigation — and on a cold
 * start deep-linked to /calendar, the first paint of the whole shell — until
 * the chunk has arrived. This paints the shell at once with a skeleton in the
 * pane, and `.preload()` (prefetch.ts: hover, focus or touch of a link to the
 * route, and idle time after the first paint) makes the chunk arrive before
 * the click in practice.
 */

/** Skeleton, not a spinner, while a route chunk streams in -- and only after 150 ms. */
function RouteSkeleton() {
  return (
    <div className="pane skeleton-deferred" style={{ padding: 'var(--space-6)', gap: 'var(--space-4)' }}>
      <Skeleton width="14rem" height="1.25rem" />
      <Skeleton width="100%" height="0.8rem" />
      <Skeleton width="85%" height="0.8rem" />
      <Skeleton width="92%" height="0.8rem" />
    </div>
  );
}

// eslint-disable-next-line @typescript-eslint/no-explicit-any
function lazyView<M extends Record<K, ComponentType<any>>, K extends keyof M & string>(
  load: () => Promise<M>,
  name: K,
) {
  return lazyComponent(load, name, <RouteSkeleton />);
}

// Mail is the landing view, so it ships in the shell chunk. Everything else is
// split out: the app shell paints without paying for calendar/contacts/settings.
export const CalendarView = lazyView(() => import('@/routes/calendar/CalendarView'), 'CalendarView');
export const ContactsView = lazyView(() => import('@/routes/contacts/ContactsView'), 'ContactsView');
export const TasksView = lazyView(() => import('@/routes/tasks/TasksView'), 'TasksView');
export const SettingsView = lazyView(() => import('@/routes/settings/SettingsView'), 'SettingsView');
export const SettingsIndex = lazyView(() => import('@/routes/settings/SettingsIndex'), 'SettingsIndex');
export const GeneralSettings = lazyView(() => import('@/routes/settings/GeneralSettings'), 'GeneralSettings');
export const AccountsSettings = lazyView(() => import('@/routes/settings/AccountsSettings'), 'AccountsSettings');
export const FiltersSettings = lazyView(() => import('@/routes/settings/FiltersSettings'), 'FiltersSettings');
export const VacationSettings = lazyView(() => import('@/routes/settings/VacationSettings'), 'VacationSettings');
export const NotificationsSettings = lazyView(
  () => import('@/routes/settings/NotificationsSettings'),
  'NotificationsSettings',
);
export const PrivacySettings = lazyView(() => import('@/routes/settings/PrivacySettings'), 'PrivacySettings');
export const AiSettings = lazyView(() => import('@/routes/settings/AiSettings'), 'AiSettings');
export const NextcloudSettings = lazyView(() => import('@/routes/settings/NextcloudSettings'), 'NextcloudSettings');
export const SessionsSettings = lazyView(() => import('@/routes/settings/SessionsSettings'), 'SessionsSettings');
// Server administration (docs/plan/13 §7): its own chunk, which only an
// administrator ever downloads -- the entry to it is drawn for nobody else.
export const AdminView = lazyView(() => import('@/routes/admin/AdminView'), 'AdminView');
// Send later and Snooze's job lists (ADR 0076 §17): reached from two folder
// rows, and they carry the composer for "Edit" -- nothing a first paint needs.
// The Outbox stays eager (router.tsx says why).
export const ScheduledView = lazyView(() => import('@/features/scheduling/ScheduledView'), 'ScheduledView');
export const SnoozedView = lazyView(() => import('@/features/scheduling/SnoozedView'), 'SnoozedView');
// The standalone message window (ADR 0020): its own chunk, since the main tab
// only pays for it when someone double-clicks a row.
export const MessageWindowView = lazyView(() => import('@/routes/message/MessageWindowView'), 'MessageWindowView');
// The popped-out composer, on the same terms.
export const ComposeWindowView = lazyView(() => import('@/routes/compose/ComposeWindowView'), 'ComposeWindowView');

/** Each settings section by its path segment, so hovering its link can warm it. */
export const SETTINGS_SECTIONS: Record<string, { preload: () => Promise<unknown> }> = {
  '': SettingsIndex,
  general: GeneralSettings,
  accounts: AccountsSettings,
  filters: FiltersSettings,
  'out-of-office': VacationSettings,
  notifications: NotificationsSettings,
  privacy: PrivacySettings,
  ai: AiSettings,
  nextcloud: NextcloudSettings,
  sessions: SessionsSettings,
};
